import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, RotateCcw, Save } from "lucide-react";

const PAGE_LABELS = {
  home: "Página inicial (Home)",
  atendimento: "Página /atendimento",
  clinic_list: "Lista de unidades (/unidades)",
  clinic_detail: "Detalhe da clínica (/unidades/:id) — usa {entity_name}",
  professional_list: "Lista de profissionais (/profissionais)",
  professional_detail: "Detalhe de profissional (/profissionais/:id) — usa {entity_name}",
  consultor_detail: "Detalhe de consultor (/consultores/:id) — usa {entity_name}",
  faq: "FAQ",
  impact: "Página /impacto",
  ranking: "Ranking",
  emergency: "Página /emergencia",
  other: "Outras páginas",
};

export default function LiaWelcomesEditor({ onClose }) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/messaging/admin/lia-welcomes")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => toast.error("Falha ao carregar saudações da LIA"));
  }, []);

  const update = (page_type, override) => {
    setItems((prev) => prev.map((x) => (x.page_type === page_type ? { ...x, override } : x)));
  };

  const clear = (page_type) => update(page_type, "");

  const save = async () => {
    setSaving(true);
    try {
      const overrides = {};
      items.forEach((x) => { if ((x.override || "").trim()) overrides[x.page_type] = x.override.trim(); });
      await api.put("/messaging/admin/lia-welcomes", { overrides });
      toast.success("Saudações da LIA atualizadas");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4" data-testid="lia-welcomes-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h2 className="font-display font-bold text-xl text-[#0B2B62]">Personalidade da LIA</h2>
            <p className="text-xs text-slate-500 mt-1">
              Deixe em branco pra usar a saudação automática. Use <code className="px-1 bg-slate-100 rounded">{`{entity_name}`}</code> pra inserir o nome da clínica/profissional resolvido pela URL.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded" data-testid="lia-welcomes-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {items.map((it) => (
            <div key={it.page_type} className="border rounded-lg p-4 bg-slate-50/50" data-testid={`lia-row-${it.page_type}`}>
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <label className="text-sm font-semibold text-[#0B2B62]">
                  {PAGE_LABELS[it.page_type] || it.page_type}
                </label>
                {it.override && (
                  <button onClick={() => clear(it.page_type)} className="text-[11px] text-slate-500 hover:text-red-600 inline-flex items-center gap-1" data-testid={`lia-reset-${it.page_type}`}>
                    <RotateCcw className="w-3 h-3" /> Voltar ao automático
                  </button>
                )}
              </div>
              <details className="mb-2 text-xs">
                <summary className="cursor-pointer text-slate-500 hover:text-slate-700">Ver saudação automática</summary>
                <div className="mt-2 p-2 rounded bg-white border text-xs text-slate-600 whitespace-pre-wrap">
                  {it.default}
                </div>
              </details>
              <textarea
                value={it.override || ""}
                onChange={(e) => update(it.page_type, e.target.value)}
                placeholder="Deixe em branco pra usar o texto automático acima..."
                className="w-full border rounded-lg p-3 text-sm min-h-[80px] bg-white focus:ring-2 focus:ring-violet-200 focus:outline-none"
                data-testid={`lia-textarea-${it.page_type}`}
              />
            </div>
          ))}
        </div>
        <div className="p-4 border-t flex justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm">Cancelar</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-[#0B2B62] text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2" data-testid="lia-welcomes-save">
            <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}
